import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowRightLeft, RefreshCw, TrendingUp } from "lucide-react";

const FLAG_EMOJIS = {
    EU: "\ud83c\uddea\ud83c\uddfa", US: "\ud83c\uddfa\ud83c\uddf8", CA: "\ud83c\udde8\ud83c\udde6",
    GB: "\ud83c\uddec\ud83c\udde7", CH: "\ud83c\udde8\ud83c\udded", JP: "\ud83c\uddef\ud83c\uddf5",
    MA: "\ud83c\uddf2\ud83c\udde6", SN: "\ud83c\uddf8\ud83c\uddf3", CN: "\ud83c\udde8\ud83c\uddf3",
    AU: "\ud83c\udde6\ud83c\uddfa", BR: "\ud83c\udde7\ud83c\uddf7", IN: "\ud83c\uddee\ud83c\uddf3",
    TR: "\ud83c\uddf9\ud83c\uddf7", MX: "\ud83c\uddf2\ud83c\uddfd", ZA: "\ud83c\uddff\ud83c\udde6",
    SE: "\ud83c\uddf8\ud83c\uddea", NO: "\ud83c\uddf3\ud83c\uddf4", DK: "\ud83c\udde9\ud83c\uddf0",
    PL: "\ud83c\uddf5\ud83c\uddf1", KR: "\ud83c\uddf0\ud83c\uddf7", SG: "\ud83c\uddf8\ud83c\uddec",
    HK: "\ud83c\udded\ud83c\uddf0", NZ: "\ud83c\uddf3\ud83c\uddff", TH: "\ud83c\uddf9\ud83c\udded",
};

const POPULAR = ["EUR", "USD", "CAD", "GBP", "CHF", "XOF", "MAD", "JPY"];

const CurrencyConverterWidget = () => {
    const { t } = useTranslation();
    const { get } = useApi();
    const [currencies, setCurrencies] = useState({});
    const [rates, setRates] = useState({});
    const [fromCurrency, setFromCurrency] = useState("EUR");
    const [toCurrency, setToCurrency] = useState("USD");
    const [amount, setAmount] = useState("1000");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rateDate, setRateDate] = useState(null);

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const data = await get("/currency/rates");
                setRates(data.rates || {});
                setCurrencies(data.currencies || {});
            } catch (err) {
                console.error("Failed to fetch rates:", err);
            }
        };
        fetchRates();
    }, [get]);

    const convert = useCallback(async () => {
        const numAmount = parseFloat(amount);
        if (!numAmount || numAmount <= 0) return;
        setLoading(true);
        try {
            const data = await get(`/currency/convert?amount=${numAmount}&from_currency=${fromCurrency}&to_currency=${toCurrency}`);
            setResult(data);
            setRateDate(data.rate_date);
        } catch {
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [get, amount, fromCurrency, toCurrency]);

    useEffect(() => {
        const timer = setTimeout(() => { convert(); }, 300);
        return () => clearTimeout(timer);
    }, [convert]);

    const swap = () => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return "—";
        return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(num);
    };

    const getFlag = (code) => {
        const info = currencies[code];
        const flagCode = info?.flag;
        return flagCode ? (FLAG_EMOJIS[flagCode] || "") : "";
    };

    const sortedCodes = Object.keys(currencies).length > 0
        ? [...POPULAR.filter(c => currencies[c]), ...Object.keys(currencies).filter(c => !POPULAR.includes(c)).sort()]
        : POPULAR;

    const fromSymbol = currencies[fromCurrency]?.symbol || fromCurrency;
    const toSymbol = currencies[toCurrency]?.symbol || toCurrency;

    return (
        <Card data-testid="currency-converter-widget">
            <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    {t("dashboard.currencyConverter")}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Amount input */}
                <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t("dashboard.amount")}</label>
                    <Input
                        type="number"
                        min="0"
                        step="any"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="font-mono text-lg"
                        data-testid="currency-amount-input"
                    />
                </div>

                {/* From / Swap / To */}
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">{t("dashboard.from")}</label>
                        <Select value={fromCurrency} onValueChange={setFromCurrency}>
                            <SelectTrigger data-testid="currency-from-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {sortedCodes.map(code => (
                                    <SelectItem key={code} value={code}>
                                        <span className="flex items-center gap-2">
                                            <span>{getFlag(code)}</span>
                                            <span className="font-mono font-medium">{code}</span>
                                            <span className="text-muted-foreground text-xs hidden sm:inline">{currencies[code]?.name}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={swap}
                        className="shrink-0 mb-0"
                        data-testid="currency-swap-btn"
                    >
                        <ArrowRightLeft className="w-4 h-4" />
                    </Button>

                    <div className="flex-1">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">{t("dashboard.to")}</label>
                        <Select value={toCurrency} onValueChange={setToCurrency}>
                            <SelectTrigger data-testid="currency-to-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {sortedCodes.map(code => (
                                    <SelectItem key={code} value={code}>
                                        <span className="flex items-center gap-2">
                                            <span>{getFlag(code)}</span>
                                            <span className="font-mono font-medium">{code}</span>
                                            <span className="text-muted-foreground text-xs hidden sm:inline">{currencies[code]?.name}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Result */}
                <div className="rounded-xl bg-secondary/70 p-4 text-center">
                    {loading ? (
                        <RefreshCw className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    ) : result ? (
                        <>
                            <p className="text-sm text-muted-foreground">
                                {formatNumber(parseFloat(amount))} {fromSymbol} =
                            </p>
                            <p className="text-2xl font-bold font-mono mt-1" data-testid="currency-result">
                                {formatNumber(result.converted_amount)} {toSymbol}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                1 {fromCurrency} = {formatNumber(result.exchange_rate)} {toCurrency}
                            </p>
                        </>
                    ) : (
                        <p className="text-muted-foreground text-sm">{t("dashboard.enterAmount")}</p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default CurrencyConverterWidget;
